import React, { useEffect, useState, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdLock as MdLockIcon,
  MdError as MdErrorIcon,
  MdCheckCircle as MdCheckCircleIcon,
  MdVisibility as MdVisibilityIcon,
  MdVisibilityOff as MdVisibilityOffIcon,
  MdPerson as MdPersonIcon,
  MdAdminPanelSettings as MdAdminPanelSettingsIcon,
  MdSecurity as MdSecurityIcon,
} from 'react-icons/md';
import { cn } from '../lib/utils';
import {
  clearFirstLoginPasswordSession,
  readFirstLoginPasswordSession,
} from '../lib/first-login-password';
import {
  EUROASIANN_GROUP_NAME,
  EUROASIANN_LOGO_DARK,
  EUROASIANN_TAGLINE,
} from '../constants/euroasiann-branding';
import mapBackground from '../assets/proper_map_bg.png';

type IconProps = { className?: string };
const MdLock = MdLockIcon as ComponentType<IconProps>;
const MdError = MdErrorIcon as ComponentType<IconProps>;
const MdCheckCircle = MdCheckCircleIcon as ComponentType<IconProps>;
const MdVisibility = MdVisibilityIcon as ComponentType<IconProps>;
const MdVisibilityOff = MdVisibilityOffIcon as ComponentType<IconProps>;
const MdPerson = MdPersonIcon as ComponentType<IconProps>;
const MdAdminPanelSettings = MdAdminPanelSettingsIcon as ComponentType<IconProps>;
const MdSecurity = MdSecurityIcon as ComponentType<IconProps>;

const API_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? '' : 'http://localhost:3000');

function apiFetchUrl(path: string): string {
  if (import.meta.env.DEV && !import.meta.env.VITE_API_URL) {
    return path;
  }
  return `${API_URL}${path}`;
}

export default function ChangePasswordFirstLogin() {
  const navigate = useNavigate();
  const session = readFirstLoginPasswordSession();
  const [email] = useState(session?.email || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!session?.token) {
      navigate('/login', { replace: true });
    }
  }, [session?.token, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!session?.token) {
      setError('Your password-change session expired. Please sign in again.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(apiFetchUrl('/api/v1/auth/complete-first-login-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: session.token,
          newPassword,
          portalType: 'admin',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to create password');
      }
      clearFirstLoginPasswordSession();
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to create password');
    } finally {
      setLoading(false);
    }
  };

  if (!session?.token) {
    return null;
  }

  return (
    <div className="h-screen flex flex-col bg-[#060d18] font-sans text-white overflow-hidden">
      <header className="relative z-30 flex justify-between items-center px-6 py-3 border-b border-white/5 backdrop-blur-md bg-[#060d18]/60 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
            <img src={EUROASIANN_LOGO_DARK} alt={EUROASIANN_GROUP_NAME} className="w-full h-full object-contain" />
          </div>
          <div className="flex flex-col">
            <h1 className="font-bold text-sm leading-tight tracking-wide text-white">{EUROASIANN_GROUP_NAME}</h1>
            <p className="text-[9px] uppercase tracking-[0.2em] text-white/50 capitalize">{EUROASIANN_TAGLINE}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/70">
          <MdAdminPanelSettings className="text-white/50" />
          <span className="font-medium">Admin Portal</span>
        </div>
      </header>

      <main className="relative z-20 flex-1 flex overflow-hidden m-3 rounded-2xl border border-white/[0.06]">
        <div className="hidden lg:flex lg:w-[55%] relative bg-[#0a1929] overflow-hidden">
          <img
            src={mapBackground}
            alt="Maritime Map"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-60 animate-[slowPan_30s_linear_infinite]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#060d18]/70 via-transparent to-[#060d18]/80 z-10" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a1929]/90 via-transparent to-[#0a1929]/40 z-10" />
          <div className="absolute inset-0 z-10 overflow-hidden opacity-20">
            <div className="absolute w-full h-[1px] bg-gradient-to-r from-transparent via-[#0088cc] to-transparent animate-[scanLine_4s_linear_infinite]" />
          </div>
          <div className="relative z-20 flex flex-col justify-end h-full p-10 pb-12">
            <div className="space-y-4">
              <h2 className="text-4xl font-bold text-white leading-tight tracking-tight">
                Secure Your<br />
                <span className="text-[#0088cc]">Admin Access</span>
              </h2>
              <p className="text-sm text-white/60 leading-relaxed max-w-md">
                Create your own password to finish account setup and unlock full administrative control of Ship Span ERP.
              </p>
              <div className="flex items-center gap-5 pt-3">
                {['First Login Setup', 'Encrypted Access', 'Admin Security'].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0088cc]" />
                    <span className="text-[11px] text-white/50 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-[45%] flex items-center justify-center p-6 lg:p-10 bg-[#0b1523] relative overflow-y-auto">
          <div className="absolute inset-0 opacity-[0.02] bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzBhOGJjZSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] pointer-events-none" />
          <div className="relative z-10 w-full max-w-sm">
            {success ? (
              <div className="text-center space-y-4">
                <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <MdCheckCircle className="w-8 h-8 text-emerald-400" />
                </div>
                <h2 className="text-2xl font-bold text-white tracking-tight">Password Created</h2>
                <p className="text-sm text-white/60">
                  Your password was saved. Redirecting to Admin Sign In…
                </p>
              </div>
            ) : (
              <>
                <div className="mb-8">
                  <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#0088cc]/25 bg-[#0088cc]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#7dd3fc]">
                    <MdSecurity className="w-3.5 h-3.5" />
                    First-time setup
                  </div>
                  <h2 className="text-2xl font-bold text-white tracking-tight">Set Your Password</h2>
                  <p className="text-sm text-white/60 mt-1.5">
                    Please create a secure password for your new Admin account before accessing the dashboard.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm animate-[shake_0.3s_ease-in-out]">
                      <MdError className="w-4 h-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider">Email</label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <MdPerson className="text-white/40 text-lg" />
                      </div>
                      <input
                        type="email"
                        value={email}
                        readOnly
                        className="w-full pl-11 pr-4 py-3 bg-white/[0.04] border border-white/10 rounded-xl text-sm text-white/80 cursor-not-allowed outline-none"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider">
                      New Password
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <MdLock className="text-white/40 text-lg group-focus-within:text-[#0088cc] transition-colors" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={8}
                        autoComplete="new-password"
                        className="w-full pl-11 pr-11 py-3 bg-white/[0.06] border border-white/15 rounded-xl text-sm text-white placeholder:text-white/30 outline-none focus:border-[#0088cc]/60 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#0088cc]/15 transition-all tracking-wider"
                        placeholder="At least 8 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-white/40 hover:text-white/70 transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <MdVisibilityOff className="text-lg" /> : <MdVisibility className="text-lg" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider">
                      Confirm Password
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                        <MdLock className="text-white/40 text-lg group-focus-within:text-[#0088cc] transition-colors" />
                      </div>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={8}
                        autoComplete="new-password"
                        className="w-full pl-11 pr-11 py-3 bg-white/[0.06] border border-white/15 rounded-xl text-sm text-white placeholder:text-white/30 outline-none focus:border-[#0088cc]/60 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#0088cc]/15 transition-all tracking-wider"
                        placeholder="Re-enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-white/40 hover:text-white/70 transition-colors"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? (
                          <MdVisibilityOff className="text-lg" />
                        ) : (
                          <MdVisibility className="text-lg" />
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={cn(
                      'w-full py-3 rounded-xl text-sm font-semibold transition-all relative overflow-hidden bg-[#0088cc] hover:bg-[#0099e6] text-white shadow-lg shadow-[#0088cc]/20 hover:shadow-[#0088cc]/30 active:scale-[0.98]',
                      loading && 'opacity-70 cursor-not-allowed',
                    )}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-[shimmer_2s_infinite] pointer-events-none" />
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      {loading ? (
                        <>
                          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                          Saving…
                        </>
                      ) : (
                        'Set Password'
                      )}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      clearFirstLoginPasswordSession();
                      navigate('/login', { replace: true });
                    }}
                    className="w-full text-xs text-white/50 hover:text-[#0088cc] transition-colors"
                  >
                    Back to login
                  </button>
                </form>

                <div className="mt-8 pt-5 border-t border-white/[0.06] text-center">
                  <p className="text-[11px] text-white/40">
                    Secure password setup for {EUROASIANN_GROUP_NAME} Admin Portal
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      <footer className="relative z-20 py-2.5 border-t border-white/[0.04] bg-[#060d18]/60 flex-shrink-0">
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 text-[11px]">
          <a
            href="https://www.euroasianngroup.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[#0088cc] hover:text-[#33aadd] transition-colors underline-offset-2 hover:underline"
          >
            Ship Span ERP - A Euroasiann product
          </a>
          <span className="text-white/20 hidden sm:inline" aria-hidden>
            ·
          </span>
          <p className="text-white/35">2026 &copy; {EUROASIANN_GROUP_NAME}. All Ships Served.</p>
        </div>
      </footer>

      <style>{`
        @keyframes slowPan { 0% { transform: scale(1.05) translateX(0) translateY(0); } 50% { transform: scale(1.1) translateX(-1%) translateY(-0.5%); } 100% { transform: scale(1.05) translateX(0) translateY(0); } }
        @keyframes scanLine { 0% { top: 0%; } 100% { top: 100%; } }
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-4px); } 75% { transform: translateX(4px); } }
      `}</style>
    </div>
  );
}
